import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Calendar } from "lucide-react";

interface BlogPostSchedulerProps {
  scheduledFor?: string;
  onScheduleChange: (scheduledDate: string | null) => void;
  disabled?: boolean;
}

export function BlogPostScheduler({ 
  scheduledFor, 
  onScheduleChange, 
  disabled = false 
}: BlogPostSchedulerProps) {
  const [isScheduled, setIsScheduled] = useState(!!scheduledFor);
  const [scheduleDateTime, setScheduleDateTime] = useState(
    scheduledFor ? new Date(scheduledFor).toISOString().slice(0, 16) : ""
  );

  const handleScheduleToggle = () => {
    const newIsScheduled = !isScheduled;
    setIsScheduled(newIsScheduled);
    
    if (!newIsScheduled) {
      setScheduleDateTime("");
      onScheduleChange(null);
    } else {
      // Set default to tomorrow at 9 AM
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      const defaultDateTime = tomorrow.toISOString().slice(0, 16);
      setScheduleDateTime(defaultDateTime);
      onScheduleChange(defaultDateTime);
    }
  };

  const handleDateTimeChange = (value: string) => {
    setScheduleDateTime(value);
    onScheduleChange(value || null);
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Agendamento de Publicação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="schedule-post"
            checked={isScheduled}
            onChange={handleScheduleToggle}
            disabled={disabled}
            className="rounded border-gray-300"
          />
          <Label htmlFor="schedule-post" className="text-sm font-medium">
            Agendar publicação para data e hora específica
          </Label>
        </div>

        {isScheduled && (
          <div className="space-y-2">
            <Label htmlFor="schedule-datetime" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Data e Hora de Publicação
            </Label>
            <Input
              id="schedule-datetime"
              type="datetime-local"
              value={scheduleDateTime}
              onChange={(e) => handleDateTimeChange(e.target.value)}
              disabled={disabled}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full"
            />
            <p className="text-xs text-gray-500">
              O post será publicado automaticamente na data e hora selecionadas.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
